import TextField from '@common_forms/TextField';
import Select from '@common_forms/Select';
import Typography from '@common_typography';
import Image from 'next/image';
import _ from 'lodash';
import cx from 'classnames';
import { COLORS } from '@theme_vars';
import Checkbox from '@common_forms/CheckBox';
import { getLoginInfo } from '@helper_auth';

const CoreApiComponent = (props) => {
    const {
        t, formik, onValuesChange = () => {}, installment_options,
        onError = () => {}, yearList, months, checkout, isCoreApiXendit,
    } = props;

    const years = yearList();
    const checkIsFieldError = (name = '') => !!(formik.touched[name] && formik.errors[name]);
    const fieldErrorMessage = (name = '') => (formik.touched[name] && formik.errors[name]) || null;

    const [cardNumber, setCardNumber] = React.useState(formik.values.card_number);
    const [cvv, setCvv] = React.useState(formik.values.card_cvv);
    const [useCustomerInfo, setIsUseCustomerInfo] = React.useState(true);
    const isLogin = getLoginInfo();

    const firstName = isLogin ? checkout?.data?.customer?.firstname : checkout?.data?.cart?.billing_address?.firstname;
    const lastName = isLogin ? checkout?.data?.customer?.lastname : checkout?.data?.cart?.billing_address?.lastname;
    const email = isLogin ? checkout?.data?.customer?.email : checkout?.data?.cart?.email;
    const phoneNumber = checkout?.data?.cart?.billing_address?.telephone;

    const handleCardNumberChange = (e) => {
        const { value } = e.target;
        // Regular expression to format credit card number with spaces
        const formattedCardNumber = value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim(); // value.replace(/[^\d]/g, '').replace(/(.{4})/g, '$1 ').slice(0, 19);
        setCardNumber(formattedCardNumber);
        formik.setFieldValue('card_number', formattedCardNumber.replace(/\s/g, ''));
    };

    const handleCvvChange = (e) => {
        const { value } = e.target;
        // Allow only digits and limit to 3 characters
        const formattedCvv = value.slice(0, 3).replace(/\D/g, '');
        setCvv(formattedCvv);
        formik.setFieldValue('card_cvv', formattedCvv);
    };

    const toggleCheckbox = () => {
        setIsUseCustomerInfo(!useCustomerInfo);
    };
    const handleUseCustomerInfoForCardData = () => {
        formik.setFieldValue('card_holder_firstname', firstName);
        formik.setFieldValue('card_holder_lastname', lastName);
        formik.setFieldValue('card_holder_email', email);
        formik.setFieldValue('card_holder_phone_number', phoneNumber);
    };

    React.useEffect(() => {
        if (cardNumber) {
            const formatted = cardNumber.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
            setCardNumber(formatted);
        }
    }, [cardNumber]);

    React.useEffect(() => {
        if (useCustomerInfo) {
            handleUseCustomerInfoForCardData();
        }
    }, [useCustomerInfo, email]);

    return (
        <>
            <div className="wrapper_card-input max-w-[480px] py-3">
                <div className="flex justify-between mb-5">
                    <Typography className="font-normal !text-[15px] !leading-[22px] tracking-[normal] !text-neutral-black" variant="label">
                        {t('common:creditCard:cardInformation')}
                    </Typography>
                    <div className="payment-logos flex">
                        <Image
                            src="/assets/img/logo-mastercard.svg"
                            width={46}
                            height={25}
                            alt="mastercard"
                        />
                        <Image
                            src="/assets/img/logo-visa.svg"
                            width={46}
                            height={25}
                            alt="visa"
                        />
                        <Image
                            src="/assets/img/logo-jcb.svg"
                            width={46}
                            height={25}
                            alt="jcb"
                        />
                    </div>
                </div>
                <div className="swift-card-input flex flex-col gap-4">
                    <TextField
                        className="border border-neutral-300 w-full"
                        classWrapper="basis-full"
                        name="card_number"
                        required
                        label={t('common:creditCard:cardNumber')}
                        value={cardNumber}
                        onChange={handleCardNumberChange}
                        maxLength={19}
                        absolute={false}
                        hintProps={{
                            displayHintText: checkIsFieldError('card_number'),
                            hintType: checkIsFieldError('card_number') ? 'error' : '',
                            hintText: fieldErrorMessage('card_number'),
                            className: cx('my-2'),
                        }}
                    />
                    <div className="flex gap-4 mobile:max-sm:flex-col">
                        <div className="flex gap-x-2 justify-between tablet:basis-5/6 tablet:w-5/6">
                            <Select
                                className={cx('select-month')}
                                classNameLabel={cx('!capitalize')}
                                textFiledProps={{
                                    className: cx(
                                        'w-full h-10',
                                    ),
                                    absolute: false,
                                }}
                                optionProps={{
                                    className: 'max-h-[200px] overflow-y-auto min-w-max options-wrapper',
                                }}
                                options={months}
                                label={`MM - ${t('common:creditCard:month')}`}
                                placeholder={t('common:creditCard:month')}
                                name="card_exp_month"
                                required
                                value={formik.values.card_exp_month}
                                onChange={(e) => formik.setFieldValue('card_exp_month', e)}
                                error={checkIsFieldError('card_exp_month')}
                                errorMessage={fieldErrorMessage('card_exp_month')}
                                useValueAsLabelInMobile
                            />
                            <Select
                                className={cx('select-month')}
                                classNameLabel={cx('!capitalize')}
                                textFiledProps={{
                                    className: cx(
                                        'w-full h-10',
                                    ),
                                    absolute: false,
                                }}
                                optionProps={{
                                    className: 'max-h-[200px] overflow-y-auto min-w-max options-wrapper',
                                }}
                                options={years}
                                label={t('common:creditCard:year')}
                                placeholder={t('common:creditCard:year')}
                                name="card_exp_year"
                                required
                                value={formik.values.card_exp_year}
                                onChange={(e) => formik.setFieldValue('card_exp_year', e)}
                                error={checkIsFieldError('card_exp_year')}
                                errorMessage={fieldErrorMessage('card_exp_year')}
                            />
                        </div>
                        <div className="tablet:basis-1/6 tablet:w-1/6">
                            <TextField
                                className="border border-neutral-300 w-full"
                                name="12"
                                required
                                label={t('common:creditCard:cvv')}
                                value={cvv}
                                onChange={handleCvvChange}
                                maxLength={3}
                                type="password"
                                absolute={false}
                                hintProps={{
                                    displayHintText: checkIsFieldError('card_cvv'),
                                    hintType: checkIsFieldError('card_cvv') ? 'error' : '',
                                    hintText: fieldErrorMessage('card_cvv'),
                                    className: cx('my-2'),
                                }}
                            />
                        </div>
                    </div>
                    {!_.isEmpty(installment_options) ? (
                        <Select
                            className="select-tenor basis-full"
                            classNameLabel={cx('!capitalize')}
                            textFiledProps={{
                                className: cx(
                                    'w-full',
                                ),
                                absolute: false,
                            }}
                            optionProps={{
                                className: 'max-h-[200px] overflow-y-auto',
                            }}
                            options={installment_options}
                            label={t('common:creditCard:selectInstallmentSchema')}
                            placeholder={t('common:creditCard:selectInstallment')}
                            name="tenor"
                            required
                            value={formik.values.tenor}
                            onChange={(e) => formik.setFieldValue('tenor', e)}
                            error={checkIsFieldError('tenor')}
                            errorMessage={fieldErrorMessage('tenor')}
                        />
                    ) : null}
                    {onValuesChange(formik.values)}
                    {onError(formik.errors)}

                    {isCoreApiXendit ? (
                        <>
                            <div className="block">
                                <Checkbox
                                    id="checkbox-use-customer-info"
                                    variant="single"
                                    label={t('common:creditCard:useCustomerInfoForCardHolder')}
                                    checked={useCustomerInfo}
                                    name="useCustomerInfo"
                                    onChange={() => {
                                        toggleCheckbox();
                                        handleUseCustomerInfoForCardData();
                                    }}
                                    classNames={{
                                        checkboxContainerClasses: cx('flex', 'flex-row'),
                                        checkboxClasses: cx('cursor-pointer', 'min-w-5', 'focus:shadow-none'),
                                    }}
                                >
                                    <label className="mt-[-2px]">
                                        <Typography>{t('common:creditCard:useCustomerInfoForCardHolder')}</Typography>
                                    </label>
                                </Checkbox>
                            </div>

                            {!useCustomerInfo ? (
                                <>
                                    <TextField
                                        className="border border-neutral-300 w-full"
                                        classWrapper="basis-full"
                                        name="card_holder_firstname"
                                        required
                                        label={t('common:creditCard:cardHolderFirstname')}
                                        value={formik.values.card_holder_firstname}
                                        onChange={formik.handleChange}
                                        maxLength={19}
                                        absolute={false}
                                        hintProps={{
                                            displayHintText: checkIsFieldError('card_holder_firstname'),
                                            hintType: checkIsFieldError('card_holder_firstname') ? 'error' : '',
                                            hintText: fieldErrorMessage('card_holder_firstname'),
                                            className: cx('my-2'),
                                        }}
                                    />
                                    <TextField
                                        className="border border-neutral-300 w-full"
                                        classWrapper="basis-full"
                                        name="card_holder_lastname"
                                        required
                                        label={t('common:creditCard:cardHolderLastname')}
                                        value={formik.values.card_holder_lastname}
                                        onChange={formik.handleChange}
                                        maxLength={19}
                                        absolute={false}
                                        hintProps={{
                                            displayHintText: checkIsFieldError('card_holder_lastname'),
                                            hintType: checkIsFieldError('card_holder_lastname') ? 'error' : '',
                                            hintText: fieldErrorMessage('card_holder_lastname'),
                                            className: cx('my-2'),
                                        }}
                                    />
                                    <TextField
                                        className="border border-neutral-300 w-full"
                                        classWrapper="basis-full"
                                        name="card_holder_email"
                                        required
                                        label={t('common:creditCard:cardHolderEmail')}
                                        value={formik.values.card_holder_email}
                                        onChange={formik.handleChange}
                                        maxLength={19}
                                        absolute={false}
                                        hintProps={{
                                            displayHintText: checkIsFieldError('card_holder_email'),
                                            hintType: checkIsFieldError('card_holder_email') ? 'error' : '',
                                            hintText: fieldErrorMessage('card_holder_email'),
                                            className: cx('my-2'),
                                        }}
                                    />
                                    <TextField
                                        className="border border-neutral-300 w-full"
                                        classWrapper="basis-full"
                                        name="card_holder_phone_number"
                                        required
                                        label={t('common:creditCard:cardHolderPhoneNumber')}
                                        value={formik.values.card_holder_phone_number}
                                        onChange={formik.handleChange}
                                        maxLength={19}
                                        absolute={false}
                                        hintProps={{
                                            displayHintText: checkIsFieldError('card_holder_phone_number'),
                                            hintType: checkIsFieldError('card_holder_phone_number') ? 'error' : '',
                                            hintText: fieldErrorMessage('card_holder_phone_number'),
                                            className: cx('my-2'),
                                        }}
                                    />
                                </>
                            ) : null}
                        </>
                    ) : null}
                </div>

                <style global jsx>
                    {`
                        .options-wrapper::-webkit-scrollbar-track {
                            background: 0 0;
                        }
                        .options-wrapper::-webkit-scrollbar-thumb {
                            background-color: ${COLORS.secondary[600]};
                            border-radius: 8px;
                            border: 1px solid transparent;
                        }
                        .options-wrapper::-webkit-scrollbar {
                            width: 8px;
                        }
                        .options-wrapper {
                            overflow-x: hidden;
                            padding: 0;
                            scrollbar-width: thin;
                            scrollbar-color: ${COLORS.secondary[600]} transparent;
                        }
                    `}
                </style>
            </div>
        </>
    );
};

export default CoreApiComponent;
